import { MoreVert } from "@mui/icons-material";
import { Divider, IconButton } from "@mui/material";
import theme from "Theme/AppTheme";
import { AcxViewSqlDialog } from "components/UI/AcxViewSqlDialog";
import { AcxMenuItem, AcxMenuV2 } from "components/UI/Menu/AcxMenu";
import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import { styleClsx } from "utils/styleClsx";
import { ChartPinToDashboardDialog } from "../ChartCard/ChartMenu/ChartPinToDashboardDialog";
import { useCurrentDashboard } from "../../useCurrentDashboard";
import {
    defaultDrawerOffset,
    LayoutDrawerStore,
} from "stores/Layout/LayoutDrawerStore";
import {
    ISignalsChartDefinition,
    SignalsObjectType,
} from "../../SignalsModels";
import { AuthStore } from "stores/AuthStore";
import SignalsPageStore from "../../Store/SignalsPageStore";
import UniqueCardStore from "./UniqueCardStore";
import { UniqueCardConfirmDelete } from "./UniqueCardConfirmDelete";
import ObjectConfigurationDrawer from "../ObjectConfiguration/ObjectConfigurationDrawer";
import { useObjectMenu } from "../useObjectMenu";

type Props = {
    store: UniqueCardStore<any>;
    iconButtonStyle?: React.CSSProperties;
};

export const UniqueCardMenu = observer(({ store, iconButtonStyle }: Props) => {
    const currentDashboard = useCurrentDashboard();
    const authStore = useStore(AuthStore);
    const signalsPageStore = useStore(SignalsPageStore);
    const drawerStore = useStore(LayoutDrawerStore);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [sqlDialogOpen, setSqlDialogOpen] = useState(false);
    const [pinDialogOpen, setPinDialogOpen] = useState(false);
    const isRefreshing = store.isRefreshing;

    const {
        menuAnchor,
        isMenuOpen,
        setIsMenuOpen,
        handleMenuButtonClick,
        handleMenuButtonKeyDown,
    } = useObjectMenu();
    const sqlQuery = (store.data as { sqlQuery?: string } | undefined)
        ?.sqlQuery;
    const showSql = !!sqlQuery?.length && authStore.isUserUltra();

    const cardDefinition = useMemo(() => {
        return currentDashboard?.signalsObjects.find(
            (obj) =>
                obj.id === store.id &&
                obj.objectType === SignalsObjectType.Unique,
        );
    }, [currentDashboard?.signalsObjects, store.id]);

    const canDelete = store.isDeletable;
    const canConfigure = store.isEditable;

    const hasPinTargets = signalsPageStore.pinTargetDashboards.some(
        (dash) => dash.id !== currentDashboard?.id,
    );

    const pinDisabled = !cardDefinition || !hasPinTargets;
    const shouldHideMenu =
        currentDashboard?.isPresentationMode ||
        !store.config.renderOptions.menu;

    useEffect(() => {
        if (!isRefreshing) return;
        setIsMenuOpen(false);
    }, [isRefreshing, setIsMenuOpen]);

    const openConfigDrawer = () => {
        drawerStore.closeAndResetDrawer();
        drawerStore.restoreDefaults();
        drawerStore.setOffsetPixels(defaultDrawerOffset);
        drawerStore.setCustomRootStyles({
            zIndex: theme.zIndex.appBar - 1,
        });
        drawerStore.setContentFactory(() => (
            <ObjectConfigurationDrawer store={store} />
        ));
        drawerStore.setOpen(true);
    };

    if (shouldHideMenu) {
        return null;
    }

    return (
        <>
            <IconButton
                id={`${store.id}-menu`}
                ref={menuAnchor}
                style={styleClsx(
                    {
                        color: isRefreshing
                            ? theme.palette.gray[400]
                            : theme.palette.black.main,
                        border: isRefreshing ? "none" : "1px solid",
                        borderColor: isRefreshing
                            ? theme.palette.gray[400]
                            : theme.palette.gray.light,
                        borderRadius: theme.shape.borderRadius,
                        backgroundColor: theme.palette.white.main,
                        cursor: isRefreshing ? "default" : "pointer",
                    },
                    iconButtonStyle,
                    isRefreshing && {
                        border: "none",
                        borderColor: "transparent",
                        cursor: "default",
                    },
                )}
                onClick={handleMenuButtonClick}
                onKeyDown={handleMenuButtonKeyDown}
                size="small"
                disabled={isRefreshing}
            >
                <MoreVert />
            </IconButton>
            <AcxMenuV2
                anchorEl={menuAnchor.current}
                open={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                disableAutoFocusItem
            >
                <AcxMenuItem
                    id={`${store.id}-refresh`}
                    onClick={() => {
                        store.runRefresh(true);
                        setIsMenuOpen(false);
                    }}
                    disabled={store.getTaskLoading("refresh")}
                >
                    Refresh
                </AcxMenuItem>
                <AcxMenuItem
                    id={`${store.id}-pin-to-dashboard`}
                    onClick={() => {
                        setPinDialogOpen(true);
                        setIsMenuOpen(false);
                    }}
                    disabled={pinDisabled}
                >
                    Pin to dashboard
                </AcxMenuItem>
                {canConfigure && (
                    <AcxMenuItem
                        id={`${store.id}-configure`}
                        onClick={() => {
                            openConfigDrawer();
                            setIsMenuOpen(false);
                        }}
                        disabled={isRefreshing}
                    >
                        Configure
                    </AcxMenuItem>
                )}
                {showSql && <Divider style={{ margin: "0px" }} />}
                {showSql && (
                    <AcxMenuItem
                        id={`${store.id}-sql`}
                        onClick={() => {
                            setSqlDialogOpen(true);
                            setIsMenuOpen(false);
                        }}
                    >
                        View SQL
                    </AcxMenuItem>
                )}
                {canDelete && <Divider style={{ margin: "0px" }} />}
                {canDelete && (
                    <AcxMenuItem
                        id={`${store.id}-delete`}
                        onClick={() => {
                            setDeleteDialogOpen(true);
                            setIsMenuOpen(false);
                        }}
                        sx={{ color: theme.palette.error.main }}
                    >
                        Delete
                    </AcxMenuItem>
                )}
            </AcxMenuV2>
            <UniqueCardConfirmDelete
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                store={store}
            />
            <ChartPinToDashboardDialog
                open={pinDialogOpen}
                onClose={() => setPinDialogOpen(false)}
                chartDefinition={
                    cardDefinition as ISignalsChartDefinition | undefined
                }
                pinState={store}
            />
            {showSql && (
                <AcxViewSqlDialog
                    sqlStatements={[sqlQuery ?? ""]}
                    title={`SQL Query for ${store.title ?? "Card"}`}
                    isModalOpen={sqlDialogOpen}
                    onClose={() => setSqlDialogOpen(false)}
                />
            )}
        </>
    );
});

export default UniqueCardMenu;
